/// <amd-module name="Repos/Views/BranchCompare/BranchCompareService" />
import { IChangeCollection } from "Repos/ChangesCommon/Contracts";
import { getChangesCollection } from "Repos/Changes/Util";
import { GitQueryCommitsCriteria, GitVersionDescriptor, VersionControlChangeType } from "Repos/Common/Contracts/Git";
import { commitToVersionDescriptor, getVersionString, gitVersionStringToVersionDescriptor } from "Repos/Common/Util/Version";
import { getGitRepository } from "Repos/Common/VCViewModelDataProviderService";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { getGitClient } from "./RestClient/Git";
import { IGitNavigationService, getGitNavigationService } from "Repos/ChangesCommon/ChangesNavigationService";

const diffCommitsDataProviderId = "ms.vss-code-web.diff-commits-data-provider";

interface GitChangeItem {
    isFolder: boolean;
    isSymLink: boolean;
    path?: string;
    serverItem?: string;
}

interface GitCommitChange {
    changeType: VersionControlChangeType;
    commitId: string;
    item: GitChangeItem;
}

export interface IBranchCompareDiffData {
    allChangesIncluded: boolean;
    changeCounts: { [changeType: number]: number };
    changes: GitCommitChange[];
    commitId: { full: string; short: string };
}

export interface IBranchCompareService extends IVssService {
    getBaseVersion(): GitVersionDescriptor;
    getChanges(): IReadonlyObservableValue<IChangeCollection<GitVersionDescriptor> | undefined>;
    getSelectedTabId(): IReadonlyObservableValue<string>;
    getTargetVersion(): GitVersionDescriptor;
    setSelectedTabId(selectedTabId: string): void;
}

class GitBranchCompareService extends VssService implements IBranchCompareService {
    private changes = new ObservableValue<IChangeCollection<GitVersionDescriptor> | undefined>(undefined);
    private baseVersion: GitVersionDescriptor;
    private maxNumberOfChanges = 1000;
    private targetVersion: GitVersionDescriptor;
    private selectedTabId: ObservableValue<string>;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const pageStateHistoryService = pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        const queryParams = pageStateHistoryService.getProjectedState();

        this.selectedTabId = new ObservableValue(queryParams["_a"] || "commits");
        pageStateHistoryService.registerObservable(
            this.selectedTabId,
            "_a",
            HistoryBehavior.replace,
            value => (value === "commits" ? "" : value),
            fromUrl => fromUrl || "commits"
        );

        this.baseVersion = gitVersionStringToVersionDescriptor(queryParams["baseVersion"]);
        this.targetVersion = gitVersionStringToVersionDescriptor(queryParams["targetVersion"]);

        const navigationService: IGitNavigationService = getGitNavigationService(this.pageContext);
        navigationService.setChanges(this.changes);

        this.fetchChanges(this.baseVersion, this.targetVersion);
    }

    public getBaseVersion(): GitVersionDescriptor {
        return this.baseVersion;
    }

    public getChanges(): IReadonlyObservableValue<IChangeCollection<GitVersionDescriptor> | undefined> {
        return this.changes;
    }

    public getSelectedTabId(): IReadonlyObservableValue<string> {
        return this.selectedTabId;
    }

    public getTargetVersion(): GitVersionDescriptor {
        return this.targetVersion;
    }

    public setSelectedTabId(selectedTabId: string): void {
        this.selectedTabId.value = selectedTabId;
    }

    private async fetchChanges(baseVersion: GitVersionDescriptor, targetVersion: GitVersionDescriptor) {
        const repository = getGitRepository(this.pageContext)!;

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data = await contributionService.getDataAsync<IBranchCompareDiffData>(diffCommitsDataProviderId, {
            baseVersion: getVersionString(baseVersion),
            maxNumberOfChanges: this.maxNumberOfChanges,
            repositoryId: repository.id,
            skipCount: 0,
            targetVersion: getVersionString(targetVersion)
        });

        if (data) {
            const commits = await getGitClient(this.pageContext).getCommitsBatch(
                {
                    $top: 1,
                    compareVersion: targetVersion,
                    itemVersion: baseVersion
                } as GitQueryCommitsCriteria,
                repository.id,
                repository.project.id
            );
            const commitId = commits.length ? commits[0].commitId : "";

            this.changes.value = getChangesCollection(
                data.changes.map(c => {
                    return {
                        changeType: c.changeType,
                        commitId: c.commitId,
                        item: {
                            isFolder: c.item.isFolder,
                            isSymLink: c.item.isSymLink,
                            originalPath: c.item.serverItem,
                            path: c.item.path || c.item.serverItem!
                        }
                    };
                }),
                repository.name,
                commitId,
                commitToVersionDescriptor(commitId),
                baseVersion,
                !data.allChangesIncluded
                    ? () => {
                          this.maxNumberOfChanges = this.maxNumberOfChanges * 2;
                          this.fetchChanges(baseVersion, targetVersion);
                      }
                    : undefined
            );
        }
    }
}

const serviceName = "IBranchCompareService";
export function getBranchCompareService(context: IVssPageContext) {
    return context.getService<IBranchCompareService>(serviceName);
}

Services.add(serviceName, { serviceFactory: GitBranchCompareService });
